import { useState, useMemo, useEffect } from "react";
import WithAuth from "../../../hoc/WithAuth";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Plus, Edit, Eye } from "lucide-react";

import { Order } from "../../../types/order";
import { useGetOrderStatusCount, useMyOrderList } from "../../../api/order";
import { DataTable } from "../../../components/ui/data-table";
import { formatDateToISO } from "../../../util/date-formatter";
import { FilterTabs } from "../../../components/ui/FilterTabs";
import { AppButton } from "../../../components/ui/AppButton";
import {
  getStatusColor,
  mapStatusToStandard,
} from "../../../util/status-formatter";
import { Icons } from "../../../assets/images/icons";
import { useTablePrint } from "../../../hooks/useTablePrint";
import { ExportButton } from "../../../components/ui/ExportButton";

function MyOrdersPage() {
  const navigate = useNavigate();

  /** State */
  const [searchText, setSearchText] = useState<string>("");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [countAll, setCountAll] = useState(0);

  /** API */
  const { data: orders, isLoading } = useMyOrderList({
    status: activeFilter === "all" ? undefined : activeFilter.toUpperCase(),
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });

  const { data: statuses } = useGetOrderStatusCount();
  const { handlePrint } = useTablePrint();

  /** Transform orders for table */
  const transformedOrders = useMemo((): Order[] => {
    if (!orders?.content) return [];
    return orders.content.map((item: Order) => ({
      id: item.id,
      product: item.product || "N/A",
      startingDate: item.startingDate,
      dueDate: item.dueDate,
      orderAmount: item.orderAmount || 0,
      status: mapStatusToStandard(item.status?.toLowerCase()), // for display only
    }));
  }, [orders]);

  /** Filter counts */
  const filterCounts = useMemo(() => {
    const statusData = statuses?.data?.data;
    return {
      all: orders?.totalElements || 0,
      accepted: statusData?.accepted || 0,
      pending: statusData?.pending || 0,
      rejected: statusData?.rejected || 0,
    };
  }, [statuses, orders]);

  const filterTabs = [
    { value: "all", label: "All", count: countAll },
    { value: "accepted", label: "Accepted", count: filterCounts.accepted },
    { value: "pending", label: "Pending", count: filterCounts.pending },
    { value: "rejected", label: "Rejected", count: filterCounts.rejected },
  ];

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleCreate = () => navigate("/create-order");
  const handleEditOrder = (order: Order) => navigate(`/edit-order/${order.id}`);
  const handleRowClick = (order: Order) => navigate(`/my-orders/${order.id}`);

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (activeFilter === "all") setCountAll(orders?.totalElements || 0);
  }, [activeFilter, orders?.totalElements]);

  /** Columns */
  const orderColumns = [
    {
      key: "product",
      title: "Product",
      dataIndex: "product",
      align: "left" as const,
    },
    {
      key: "startingDate",
      title: "Start Date",
      dataIndex: "startingDate",
      align: "left" as const,
      render: (v: string) => formatDateToISO(v),
    },
    {
      key: "dueDate",
      title: "Due Date",
      dataIndex: "dueDate",
      align: "left" as const,
      render: (v: string) => formatDateToISO(v),
    },
    {
      key: "orderAmount",
      title: "Quantity (Kg)",
      dataIndex: "orderAmount",
      align: "left" as const,
      render: (v: number) => v?.toLocaleString(),
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      align: "left" as const,
      render: (value: string) => (
        <div
          className="px-3 py-1 max-w-[85px] rounded-md text-center text-xs font-medium capitalize"
          style={{
            color: getStatusColor(value, "text"),
            backgroundColor: getStatusColor(value, "bg"),
          }}
        >
          {value}
        </div>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      align: "center" as const,
      render: (_: any, record: Order) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleEditOrder(record);
          }}
          disabled={record.status.toLowerCase() !== "pending"}
          className="h-8 p-0 w-full gap-2"
        >
          <img src={Icons.pencilEdit} alt="edit" className="h-6 w-6" /> Edit
        </Button>
      ),
    },
  ];

  /** Mobile data */
  const mobileData = transformedOrders;

  /** Mobile view */
  const MobileOrdersView = () => (
    <div className="w-full divide-y divide-gray-200 md:hidden px-4 mb-4">
      {/** Search */}
      <div className="py-4 sticky top-0 bg-background z-10">
        <Input
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search products by name"
          className="bg-muted"
        />
      </div>

      {/** Filter Tabs */}
      <div className="py-4 flex gap-2 overflow-x-auto">
        {filterTabs.map((filter) => (
          <Button
            key={filter.value}
            variant={activeFilter === filter.value ? "default" : "outline"}
            size="sm"
            onClick={() => handleFilterChange(filter.value)}
            className="whitespace-nowrap"
          >
            {filter.label} ({filter.count})
          </Button>
        ))}
      </div>

      {/** Orders List */}
      {mobileData.map((item) => (
        <div
          key={item.id}
          className="py-4 cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => handleRowClick(item)}
        >
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-start">
              <div className="font-semibold text-lg text-foreground">
                {item.product}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRowClick(item);
                  }}
                  className="h-8 w-8 p-0"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditOrder(item);
                  }}
                  disabled={item.status.toLowerCase() !== "pending"}
                  className="h-8 w-8 p-0"
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="text-sm text-muted-foreground">
              {item.startingDate?.split("T")[0]} - {item.dueDate?.split("T")[0]}
            </div>

            <div className="flex justify-between items-center">
              <div className="font-medium text-foreground">
                {item.orderAmount?.toLocaleString()} qt
              </div>
              <div
                className="px-3 py-1 rounded-full text-xs font-medium capitalize"
                style={{
                  color: getStatusColor(item.status),
                  backgroundColor: `${getStatusColor(item.status)}22`,
                }}
              >
                {item.status}
              </div>
            </div>
          </div>
        </div>
      ))}

      {/** No results */}
      {mobileData.length === 0 && !isLoading && (
        <div className="text-center py-8 text-muted-foreground">
          No orders found
        </div>
      )}

      {/** Loading */}
      {isLoading && (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="py-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/4"></div>
            </div>
          ))}
        </div>
      )}

      {/** Pagination */}
      {orders?.totalPages > 1 && (
        <div className="py-4 flex justify-center">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </Button>
            <span className="flex items-center px-3 text-sm">
              Page {currentPage} of {orders.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= orders.totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/** FAB */}
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={handleCreate}
          size="lg"
          className="rounded-full h-14 w-14 shadow-lg bg-green-500 hover:bg-green-600 text-white"
        >
          <Plus className="h-6 w-6" />
        </Button>
      </div>
    </div>
  );

  return (
    <div
      className="bg-transparent lg:bg-white"
      style={{ borderRadius: 8.5, marginTop: 25 }}
    >
      {/** Header Filters */}
      <div className="space-y-4 mb-6">
        <FilterTabs
          tabs={filterTabs}
          activeTab={activeFilter}
          onTabChange={handleFilterChange}
          className="w-full lg:w-auto"
        />
      </div>

      <div className="flex flex-col sm:flex-row justify-between">
        <div className="relative w-full lg:w-64">
          <Input
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search products by name"
            className="pl-10 rounded-2xl w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2">
          <AppButton
            customIcon={Icons.printIcon}
            variant="outline"
            onClick={() =>
              handlePrint(orderColumns, transformedOrders, "Order Report")
            }
            className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            Print
          </AppButton>

          <ExportButton
            columns={orderColumns}
            data={transformedOrders}
            fileName="orders"
            title="Order Report"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: orders?.totalElements ?? 0,
            }}
            customIcon={Icons.fileExport}
            className="bg-[#DDDDDD] hover:bg-gray-300"
          />

          <AppButton
            customIcon={Icons.addCircle}
            variant="success"
            onClick={handleCreate}
          >
            Create Order
          </AppButton>
        </div>
      </div>

      {/** Desktop Table */}
      <div className="hidden md:block">
        <DataTable<Order>
          data={transformedOrders}
          columns={orderColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: orders?.totalElements ?? 0,
            onChange: handlePaginationChange,
          }}
          onRowClick={handleRowClick}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No orders found"
          rowKey="id"
        />
      </div>

      {/** Mobile Table */}
      <MobileOrdersView />
    </div>
  );
}

export default WithAuth(MyOrdersPage);
